import React from 'react';
import classnames from 'classnames';
import Dropzone from 'react-dropzone';

import Modal from 'ub/components/modal';
import { Button } from 'ub/components/button';
import Toolbar from './toolbar';
import AssetGrid from './asset-grid';
import UploadButton from './upload-button';
import DeleteConfirmation from './delete-confirmation';
import planFeatures from 'ub/plan-features.js';

import {
  itemsPropType,
  assetSourcePropType,
  sortByPropType,
  assetTypePropType
} from './prop-types';

import styles from './asset-library.scss';
import emptyLibrarySvg from 'ub/icons/asset-library/empty-library.svg';

import { trackUserEvent } from 'ub/ui-user-tracking';

const SvgError = () => (
  <div>
    <br />
    <p>
      {`Oh snap, uploading SVG files is not available on your account
      because you're on a grandfathered plan. Please `}
      <a
        href="/change_subscription_plan"
        target="_blank"
        rel="noopener noreferrer"
      >
        update your subscription
      </a>
      {' to unlock SVG image support.'}
    </p>
  </div>
);

const UploadError = ({ failedUploads }) => {
  const showSvgError =
    !planFeatures.isFeatureEnabled('svgUpload') &&
    failedUploads.some(failError => failError.includes('.svg'));

  const svgError = showSvgError ? <SvgError /> : null;

  return (
    <div className={styles.uploadError}>
      <p>Sorry, there was an error uploading the following: </p>
      <p>
        {failedUploads.map(uploadStr => <li key={uploadStr}>{uploadStr}</li>)}
      </p>
      {svgError}
    </div>
  );
};

UploadError.propTypes = {
  failedUploads: React.PropTypes.array.isRequired
};

export default class AssetLibrary extends React.PureComponent {
  constructor(props) {
    super(props);

    this.handleChooseItem = this.handleChooseItem.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.dragAndDropUpload = this.dragAndDropUpload.bind(this);
    this.buttonUpload = this.buttonUpload.bind(this);
  }

  dragAndDropUpload(files) {
    trackUserEvent('assetUpload', 'dragDropAssetLibrary');
    this.props.onSelectFilesForUpload(files);
  }

  buttonUpload(files) {
    trackUserEvent('assetUpload', 'uploadButton');
    this.props.onSelectFilesForUpload(files);
  }

  handleChooseItem() {
    this.props.dispatch({ type: 'choose' });
  }

  handleClose() {
    this.props.dispatch({ type: 'close' });
  }

  getErrorMessage() {
    const {
      fetchError,
      typeTotals,
      selectedType,
      isLoading,
      searchTerm
    } = this.props;
    const isEmpty = typeTotals[selectedType] === 0 && !isLoading && !searchTerm;

    if (fetchError) {
      return (
        'We were unable to load any files. ' +
        'Please try again or contact support if the problem persists.'
      );
    } else if (isEmpty) {
      return 'Uh-oh! There are no files available.';
    } else {
      return null;
    }
  }

  render() {
    const {
      chooseAllowed,
      deleteAllowed,
      dispatch,
      failedUploads,
      isConfirmingDelete,
      isLoading,
      isUploading,
      items,
      largeThumbnails,
      loadingText,
      onScrollNearBottom,
      searchTerm,
      selectedItemIds,
      selectedSource,
      selectedType,
      sortAllowed,
      sortAscending,
      sortBy,
      sourceDescription,
      sourceTotals,
      title,
      typeTotals,
      uploadAllowed,
      uploadButtonText,
      uploadingText,
      visibleTypes
    } = this.props;

    const onlyImages =
      visibleTypes.length === 1 && visibleTypes[0] === 'images';
    const errorMessage = this.getErrorMessage();

    return (
      <Dropzone
        disableClick
        disablePreview
        activeClassName={styles.dropReady}
        style={{}} // this is required to remove default styles
        onDrop={this.dragAndDropUpload}
      >
        <Modal
          closePortal={this.handleClose}
          title={title}
          className={styles.modal}
        >
          <Toolbar
            deleteAllowed={deleteAllowed}
            deleteEnabled={selectedItemIds.length > 0 && !isConfirmingDelete}
            dispatch={dispatch}
            onlyImages={onlyImages}
            searchTerm={searchTerm}
            selectedSource={selectedSource}
            selectedType={selectedType}
            sortAllowed={sortAllowed}
            sortAscending={sortAscending}
            sortBy={sortBy}
            sourceDescription={sourceDescription}
            sourceTotals={sourceTotals}
            typeTotals={typeTotals}
            visibleTypes={visibleTypes}
          />

          <div className={styles.contents}>
            {errorMessage ? (
              <div className={styles.overlay}>
                <div
                  className={styles.emptyLibraryIcon}
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: emptyLibrarySvg }}
                />
                <div className={styles.overlayTitle}>{errorMessage}</div>
                {uploadAllowed && (
                  <div>
                    Click the button below to upload new files from your
                    computer.
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.assetGrid}>
                <AssetGrid
                  deleteAllowed={deleteAllowed}
                  dispatch={dispatch}
                  items={items}
                  largeThumbnails={largeThumbnails}
                  onScrollNearBottom={onScrollNearBottom}
                  selectedItemIds={selectedItemIds}
                  selectedSource={selectedSource}
                  sortAscending={sortAscending}
                  sortBy={sortBy}
                />
              </div>
            )}

            {isLoading && (
              <div className={styles.overlay}>
                <div
                  className={classnames('loading-icon', styles.loadingIcon)}
                />
                <div className={styles.overlayTitle}>{loadingText}</div>
              </div>
            )}

            {isUploading && (
              <div className={styles.overlay}>
                <div
                  className={classnames('loading-icon', styles.loadingIcon)}
                />
                <div className={styles.overlayTitle}>{uploadingText}</div>
                Please wait, this might take a little while.
              </div>
            )}

            {isConfirmingDelete && (
              <div
                className={classnames(
                  styles.overlay,
                  styles.deleteConfirmation
                )}
              >
                <DeleteConfirmation
                  items={selectedItemIds
                    .map(itemId => items.find(item => item.id === itemId))
                    .filter(Boolean)}
                  dispatch={dispatch}
                />
              </div>
            )}

            {failedUploads.length > 0 && (
              <UploadError failedUploads={failedUploads} />
            )}
          </div>

          <div className={styles.actionBar}>
            {uploadAllowed && (
              <UploadButton
                label={uploadButtonText}
                onlyImages={onlyImages}
                onSelectFiles={this.buttonUpload}
              />
            )}

            <Button
              className={styles.cancelButton}
              clickAction={this.handleClose}
              size="small"
              type="secondary"
            >
              Cancel
            </Button>

            <Button
              className={styles.chooseButton}
              clickAction={this.handleChooseItem}
              disabled={!chooseAllowed}
              size="small"
              type="action"
            >
              Choose
            </Button>
          </div>
        </Modal>
      </Dropzone>
    );
  }
}

AssetLibrary.propTypes = {
  chooseAllowed: React.PropTypes.bool.isRequired,
  deleteAllowed: React.PropTypes.bool.isRequired,
  dispatch: React.PropTypes.func.isRequired,
  failedUploads: React.PropTypes.arrayOf(React.PropTypes.string).isRequired,
  fetchError: React.PropTypes.instanceOf(Error),
  isConfirmingDelete: React.PropTypes.bool.isRequired,
  isLoading: React.PropTypes.bool.isRequired,
  isUploading: React.PropTypes.bool.isRequired,
  items: itemsPropType.isRequired,
  largeThumbnails: React.PropTypes.bool.isRequired,
  loadingText: React.PropTypes.string.isRequired,
  onScrollNearBottom: React.PropTypes.func,
  onSelectFilesForUpload: React.PropTypes.func.isRequired,
  searchTerm: React.PropTypes.string.isRequired,
  selectedItemIds: React.PropTypes.arrayOf(React.PropTypes.string).isRequired,
  selectedSource: assetSourcePropType.isRequired,
  selectedType: assetTypePropType.isRequired,
  sortAllowed: React.PropTypes.bool.isRequired,
  sortAscending: React.PropTypes.bool.isRequired,
  sortBy: sortByPropType.isRequired,
  sourceDescription: React.PropTypes.string,
  sourceTotals: React.PropTypes.object.isRequired,
  title: React.PropTypes.string.isRequired,
  typeTotals: React.PropTypes.object.isRequired,
  uploadAllowed: React.PropTypes.bool.isRequired,
  uploadButtonText: React.PropTypes.string,
  uploadingText: React.PropTypes.string.isRequired,
  visibleTypes: React.PropTypes.arrayOf(assetTypePropType).isRequired
};



// WEBPACK FOOTER //
// ./src/components/asset-library/asset-library.jsx